import { Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { CustomImageView } from "../../component/CustomImageView";
import { CustomTextView } from "../../component/CustomTextView";
import {
  COLOR_BLACK,
  COLOR_GREEN,
  COLOR_MAIN,
  COLOR_WHITE,
} from "../../../data/colors/Colors";
import { HorizontalOnlyContainer } from "../../component/HorizontalOnlyContainer";
import { CustomCardView } from "../../component/CustomCardView";
import useWindowDimensions from "../../../core/utils/UseWindowDimensions";
import { CustomButtonView } from "../../component/CustomButtonView";
import { ROUTE_ENDPOINTS } from "../../../data/constant/RouteConstants";

export function GameFinishedView() {
  const location = useLocation();
  const navigate = useNavigate();

  const { t, i18n } = useTranslation();

  const { height } = useWindowDimensions();
  const isMobile = height < 700;

  const teamsNameScoreModel: TeamsNameScoreModel = location.state;

  function getTitle(firstTeamScore: number, secondTeamScore: number): string {
    if (i18n.resolvedLanguage == "en") {
      return "Congrats";
    }

    const normalPool = [
      "Loser يلا مب مشكله",
      "Loser مستوى",
      "GG Loser",
      "افا Loser هاي اخرتها",
      "Loser قامت عليكم",
      "فريق Loser شدو حيلكم ثاني مره",
      "Loser يوم لك ويوم عليك",
      "Winner ماتعبت من الفوز؟",
      "Winner عوايدكم",
      "لا لا لا طلعو يلعبون Winner",
      "الصراحه فوز مستحق لـWinner",
      "بدون صياح شباب عطونا صفقه لـWinner",
    ];

    const scoreDifference700Pool = [
      "Winner شكلك فايز بالغلط",
      "Winner حظ المبتدئين",
      "Winner هاي شلون فاز؟",
      "Winner اكيد غاشين",
      "Loser يمديكم يمديكم ريماتج",
    ];

    const scoreDifference1500Pool = [
      "Loser العب مره ثانيه وبفوزك هالمره",
      "Winner ماتعبت من الفوز؟",
      "يا Winner في حد ضدكم؟",
      "شوي شوي عليهم يالغالي",
      "Winner احترم مشاعرهم",
      "Winner لا تزعله عشان مايرد البيت",
    ];

    const scoreDifference200Pool = [
      "قلنا لكم الTie Breaker يفرق",
      "يلا فريق Loser شاستفدت الحين؟",
      "فريق Winner علموهم علموهم عن قوة كاسر التعادل",
    ];

    const tiePool = [
      "من قام يتعادل؟",
      "عيد عيد القيم",
      "ثاني مره استعمل وسيلة كاسر التعادل",
    ];

    const scoreDifference = Math.abs(firstTeamScore - secondTeamScore);

    if (scoreDifference == 0) {
      return getRandomString(tiePool);
    } else if (scoreDifference == 200) {
      return getRandomString(scoreDifference200Pool);
    } else if (scoreDifference <= 700) {
      return getRandomString(scoreDifference700Pool);
    } else if (scoreDifference >= 1500) {
      return getRandomString(scoreDifference1500Pool);
    } else {
      return getRandomString(normalPool);
    }
  }

  function getRandomString(list: Array<string>): string {
    return list[Math.floor(Math.random() * (list.length + 0))];
  }

  function getWinnerName(): string {
    if (
      teamsNameScoreModel.firstTeamScore > teamsNameScoreModel.secondTeamScore
    ) {
      return teamsNameScoreModel.firstTeamName;
    }
    return teamsNameScoreModel.secondTeamName;
  }

  function getLoserName(): string {
    if (
      teamsNameScoreModel.firstTeamScore > teamsNameScoreModel.secondTeamScore
    ) {
      return teamsNameScoreModel.secondTeamName;
    }
    return teamsNameScoreModel.firstTeamName;
  }

  function getWinnerScore(): number {
    if (
      teamsNameScoreModel.firstTeamScore > teamsNameScoreModel.secondTeamScore
    ) {
      return teamsNameScoreModel.firstTeamScore;
    }
    return teamsNameScoreModel.secondTeamScore;
  }

  function getLoserScore(): number {
    if (
      teamsNameScoreModel.firstTeamScore > teamsNameScoreModel.secondTeamScore
    ) {
      return teamsNameScoreModel.secondTeamScore;
    }
    return teamsNameScoreModel.firstTeamScore;
  }

  function isTie(): boolean {
    return (
      teamsNameScoreModel.firstTeamScore == teamsNameScoreModel.secondTeamScore
    );
  }

  return (
    <HorizontalOnlyContainer>
      <Stack
        width={"100vw"}
        height={"100vh"}
        alignItems={"center"}
        justifyContent={"center"}
      >
        <CustomImageView
          width={"100%"}
          height={"100%"}
          src={require("../../../assets/images/game_ended_bg_image.jpeg")}
          props={{ position: "relative" }}
        />

        <Stack
          position={"absolute"}
          zIndex={3}
          gap={isMobile ? 1 : 6}
          width={"100vw"}
          height={"100vh"}
          alignItems={"center"}
          justifyContent={"center"}
        >
          <CustomTextView
            text={getTitle(
              teamsNameScoreModel.firstTeamScore,
              teamsNameScoreModel.secondTeamScore
            )
              .replaceAll("Winner", getWinnerName())
              .replaceAll("Loser", getLoserName())}
            variant={isMobile ? "h6" : "h3"}
            textColor={COLOR_MAIN}
            fontWeight="bold"
          />

          <CustomCardView
            width={
              isTie()
                ? isMobile
                  ? "40vw"
                  : "30vw"
                : isMobile
                ? "60vw"
                : "40vw"
            }
            padding={isMobile ? 2 : 3}
            borderRadius={4}
            children={
              <Stack
                direction={"row"}
                alignItems={"center"}
                width={"100%"}
                justifyContent={"space-evenly"}
              >
                <CustomTextView
                  text={getWinnerName()}
                  variant={isMobile ? "h6" : "h4"}
                  textColor={COLOR_WHITE}
                  fontWeight="bold"
                />

                <CustomCardView
                  padding={isMobile ? 4 : 8}
                  borderRadius={4}
                  children={
                    <CustomTextView
                      text={getWinnerScore().toString()}
                      variant={isMobile ? "h5" : "h3"}
                      textColor={COLOR_BLACK}
                      fontWeight="bold"
                    />
                  }
                  background={COLOR_WHITE}
                />
              </Stack>
            }
            background={isTie() ? COLOR_MAIN : COLOR_GREEN}
          />

          <CustomCardView
            width={isMobile ? "40vw" : "30vw"}
            borderRadius={4}
            padding={isMobile ? 2 : 3}
            children={
              <Stack
                direction={"row"}
                alignItems={"center"}
                width={"100%"}
                justifyContent={"space-evenly"}
              >
                <CustomTextView
                  text={getLoserName()}
                  variant={isMobile ? "body1" : "h6"}
                  textColor={COLOR_WHITE}
                  fontWeight="bold"
                />

                <CustomCardView
                  padding={isMobile ? 3 : 6}
                  borderRadius={4}
                  children={
                    <CustomTextView
                      text={getLoserScore().toString()}
                      variant={isMobile ? "h6" : "h5"}
                      textColor={COLOR_BLACK}
                      fontWeight="bold"
                    />
                  }
                  background={COLOR_WHITE}
                />
              </Stack>
            }
            background={isTie() ? COLOR_MAIN : "red"}
          />

          <CustomButtonView
            text={t("go_to_home_page")}
            onClick={() => {
              navigate(ROUTE_ENDPOINTS.HOME_PLAY, { replace: true });
            }}
            props={{ pl: 2, pr: 2 }}
            fontSize={isMobile ? "body2" : "h5"}
          />
        </Stack>
      </Stack>
    </HorizontalOnlyContainer>
  );
}

interface TeamsNameScoreModel {
  firstTeamName: string;
  firstTeamScore: number;
  secondTeamName: string;
  secondTeamScore: number;
}
